import type { LinkingOptions } from '@react-navigation/native';
import type { RootStackParamList } from '../navigation/types';

export interface RouteMetadata {
  title: string;
  description?: string;
  keywords?: string[];
  noIndex?: boolean;
}

export interface WebRouteConfig {
  path: string;
  exact?: boolean;
  meta?: RouteMetadata;
  redirectFrom?: string[];
}

// Flattened route configuration with clean URLs
export const webRouteConfig: Record<string, WebRouteConfig> = {
  // Root route - redirects to home
  root: {
    path: '/',
    exact: true,
    meta: {
      title: 'Coffeeplexity - Coffee Recipes, Equipment & Community',
      description:
        'Discover amazing coffee recipes, equipment reviews, and connect with coffee creators',
      keywords: ['coffee', 'recipes', 'brewing', 'equipment', 'community'],
    },
  },

  // Main tab routes (flattened)
  recommendations: {
    path: '/recommendations',
    meta: {
      title: 'Recommendations | Coffeeplexity',
      description:
        'Personalized coffee recommendations based on your preferences',
      keywords: [
        'coffee recommendations',
        'personalized',
        'AI recommendations',
      ],
    },
  },
  recipes: {
    path: '/recipes',
    meta: {
      title: 'Coffee Recipes | Coffeeplexity',
      description: 'Explore our collection of coffee brewing recipes',
      keywords: ['coffee recipes', 'brewing methods', 'coffee guides'],
    },
  },
  creators: {
    path: '/creators',
    meta: {
      title: 'Coffee Creators | Coffeeplexity',
      description: 'Meet talented coffee creators and baristas',
      keywords: ['coffee creators', 'baristas', 'coffee community'],
    },
  },
  equipment: {
    path: '/equipment',
    meta: {
      title: 'Coffee Equipment | Coffeeplexity',
      description: 'Discover and review coffee brewing equipment',
      keywords: ['coffee equipment', 'brewing gear', 'coffee tools'],
    },
  },
  profile: {
    path: '/profile',
    meta: {
      title: 'My Profile | Coffeeplexity',
      description: 'Manage your coffee profile and preferences',
      noIndex: true,
    },
  },
  home: {
    path: '/home',
    redirectFrom: ['/'],
    meta: {
      title: 'Home | Coffeeplexity',
      description: 'Discover amazing coffee recipes, equipment, and creators',
      keywords: ['coffee', 'recipes', 'brewing', 'equipment'],
    },
  },

  // Detail routes with camelCase parameters
  recipeDetail: {
    path: '/recipes/:recipeId',
    meta: {
      title: 'Coffee Recipe | Coffeeplexity',
      description: 'Learn how to brew this amazing coffee recipe',
    },
  },
  creatorProfile: {
    path: '/creators/:creatorId',
    meta: {
      title: 'Coffee Creator Profile | Coffeeplexity',
      description: 'Discover recipes and tips from this coffee creator',
    },
  },
  creatorRecipes: {
    path: '/creators/:creatorId/recipes',
    meta: {
      title: 'Creator Recipes | Coffeeplexity',
      description: 'Explore recipes by this creator',
    },
  },
  equipmentDetail: {
    path: '/equipment/:equipmentId',
    meta: {
      title: 'Coffee Equipment | Coffeeplexity',
      description: 'Learn about this coffee brewing equipment',
    },
  },

  // Nested routes with proper hierarchy
  createRecipe: {
    path: '/recipes/create',
    meta: {
      title: 'Create Recipe | Coffeeplexity',
      description: 'Share your coffee recipe with the community',
      noIndex: true,
    },
  },
  editProfile: {
    path: '/profile/edit',
    meta: {
      title: 'Edit Profile | Coffeeplexity',
      description: 'Update your profile information',
      noIndex: true,
    },
  },
  myRecipes: {
    path: '/profile/myRecipes',
    meta: {
      title: 'My Recipes | Coffeeplexity',
      description: 'Manage your coffee recipes',
      noIndex: true,
    },
  },
  settings: {
    path: '/profile/settings',
    meta: {
      title: 'Settings | Coffeeplexity',
      description: 'Manage your account settings and preferences',
      noIndex: true,
    },
  },
  notificationSettings: {
    path: '/profile/settings/notifications',
    meta: {
      title: 'Notification Settings | Coffeeplexity',
      description: 'Manage your notification preferences',
      noIndex: true,
    },
  },
  privacySecurity: {
    path: '/profile/settings/privacy',
    meta: {
      title: 'Privacy & Security | Coffeeplexity',
      description: 'Manage your privacy and security settings',
      noIndex: true,
    },
  },
  helpSupport: {
    path: '/profile/help',
    meta: {
      title: 'Help & Support | Coffeeplexity',
      description: 'Get help and support for your account',
      noIndex: true,
    },
  },
  deleteAccount: {
    path: '/profile/settings/deleteAccount',
    meta: {
      title: 'Delete Account | Coffeeplexity',
      description: 'Delete your Coffeeplexity account',
      noIndex: true,
    },
  },
  favorites: {
    path: '/profile/favorites',
    meta: {
      title: 'My Favorites | Coffeeplexity',
      description: 'View your favorite recipes and content',
      noIndex: true,
    },
  },
  roasters: {
    path: '/recipes/roasters',
    meta: {
      title: 'Coffee Roasters | Coffeeplexity',
      description: 'Find and explore coffee roasters',
      keywords: ['coffee roasters', 'coffee beans', 'specialty coffee'],
    },
  },

  // Special features and drawer routes
  journey: {
    path: '/journey',
    meta: {
      title: 'Coffee Journey | Coffeeplexity',
      description: 'Track your coffee brewing journey and progress',
    },
  },
  aiExperience: {
    path: '/aiExperiments',
    redirectFrom: ['/ai'],
    meta: {
      title: 'AI Experiments (Alpha) | Coffeeplexity',
      description: 'Explore AI-powered coffee recommendations and experiments',
    },
  },
  aiExperiments: {
    path: '/aiExperiments',
    meta: {
      title: 'AI Experiments (Alpha) | Coffeeplexity',
      description: 'Explore AI-powered coffee recommendations and experiments',
    },
  },
  termsAndConditions: {
    path: '/termsAndConditions',
    meta: {
      title: 'Terms & Conditions | Coffeeplexity',
      description: 'Read our terms and conditions',
      noIndex: true,
    },
  },
  dashboardTest: {
    path: '/dashboard/test',
    meta: {
      title: 'Dashboard Test | Coffeeplexity',
      description: 'Test dashboard functionality',
      noIndex: true,
    },
  },

  // Auth routes
  login: {
    path: '/auth/login',
    redirectFrom: ['/login'],
    meta: {
      title: 'Login | Coffeeplexity',
      description: 'Sign in to your Coffeeplexity account',
      noIndex: true,
    },
  },
  register: {
    path: '/auth/register',
    redirectFrom: ['/signup', '/register'],
    meta: {
      title: 'Sign Up | Coffeeplexity',
      description: 'Create your Coffeeplexity account',
      noIndex: true,
    },
  },
  forgotPassword: {
    path: '/auth/forgotPassword',
    meta: {
      title: 'Forgot Password | Coffeeplexity',
      description: 'Reset your Coffeeplexity password',
      noIndex: true,
    },
  },
  resetPassword: {
    path: '/auth/resetPassword',
    meta: {
      title: 'Reset Password | Coffeeplexity',
      description: 'Set a new password for your account',
      noIndex: true,
    },
  },
  verifyEmail: {
    path: '/auth/verifyEmail',
    meta: {
      title: 'Verify Email | Coffeeplexity',
      description: 'Verify your email address',
      noIndex: true,
    },
  },
};

// Production-ready linking configuration with truly flattened URLs
export function generateLinkingConfig(): LinkingOptions<RootStackParamList> {
  return {
    prefixes: [
      'coffeeplexity://',
      'https://coffeeplexity.com',
      'https://app.coffeeplexity.com',
      ...(process.env.NODE_ENV === 'development'
        ? ['http://localhost:19006', 'http://localhost:3000']
        : []),
    ],
    config: {
      initialRouteName: 'main',
      screens: {
        main: {
          path: '/',
          initialRouteName: 'home',
          screens: {
            // Home drawer screen - contains Tab Navigator on small screens
            home: {
              path: '',
              screens: {
                // Tab Navigator screens - these work for both small and large screens
                recipes: {
                  path: 'recipes',
                  screens: {
                    index: '',
                    recipeDetail: ':recipeId',
                    createRecipe: 'create',
                    roasters: 'roasters',
                  },
                },
                creators: {
                  path: 'creators',
                  screens: {
                    index: '',
                    // specific routes must come before generic :creatorId
                    creatorRecipes: ':creatorId/recipes',
                    recipeDetail: 'recipe/:recipeId',
                    creatorProfile: ':creatorId',
                  },
                },
                equipment: {
                  path: 'equipment',
                  screens: {
                    index: '',
                    equipmentDetail: ':equipmentId',
                  },
                },
                profile: {
                  path: 'profile',
                  screens: {
                    index: '',
                    editProfile: 'edit',
                    myRecipes: 'myRecipes',
                    settings: 'settings',
                    notificationSettings: 'settings/notifications',
                    deleteAccount: 'settings/deleteAccount',
                    privacySecurity: 'settings/privacy',
                    helpSupport: 'help',
                    createRecipe: 'createRecipe',
                    favorites: 'favorites',
                  },
                },
              },
            },
            // Additional flat routes (no nesting)
            recommendations: '/recommendations',
            termsAndConditions: '/termsAndConditions',
            settings: '/settings',
            helpSupport: '/helpSupport',
            // Journey with sub-screens
            journey: {
              path: 'journey',
              exact: true,
              screens: {
                index: '',
                createJourney: 'create',
                weeklyGoals: 'weeklyGoals',
                activityDetail: 'activity/:activityId',
                progressTracker: 'progress',
              },
            },
            // AI Experience with sub-screens
            aiExperience: {
              path: 'aiExperience',
              exact: true,
              screens: {
                index: '',
                aiOnboarding: 'onboarding',
                aiJourneyFlow: 'journeyFlow',
                aiAdaptiveLearning: 'adaptiveLearning',
                aiPersonalization: 'personalization',
                aiAnalytics: 'analytics',
                socialLearningAnalytics: 'socialLearning',
                intelligentSearch: 'search',
                aiAssessment: 'assessment',
                aiTutoringDashboard: 'tutoring',
                premiumAnalyticsDashboard: 'premiumAnalytics',
                peerMatching: 'peerMatching',
                adaptiveProgress: 'adaptiveProgress',
                progressInsights: 'progressInsights',
                notificationCenter: 'notifications',
                masteryVisualization: 'mastery',
              },
            },
            dashboardTest: '/dashboard/test',
          },
        },
        auth: {
          path: '/auth',
          screens: {
            login: '/login',
            register: '/register',
            forgotPassword: '/forgotPassword',
            resetPassword: '/resetPassword',
            verifyEmail: '/verifyEmail',
          },
        },
      },
    },
  } as any;
}

// Helper function to get route metadata for SEO
export function getRouteMetadata(routeName: string): RouteMetadata | undefined {
  return webRouteConfig[routeName]?.meta;
}

// Helper function to generate canonical URLs
export function getCanonicalUrl(
  routeName: string,
  params?: Record<string, string>
): string {
  const route = webRouteConfig[routeName];
  if (!route) return '/';

  let path = route.path;
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      path = path.replace(`:${key}`, value);
    });
  }

  return path;
}
